import {
  IFiguresAndTablesFile,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import {
  getOrganizationSignedPrivateAccessUrl,
  getSignedPrivateAccessUrl
} from '../api/queries';
import { pick } from 'lodash';
import { AllowedFileFormats } from '@publishing-engine/domain/types/article';
const getFileFromUrl = (url: string) =>
  url.replace(/\?.*/, '').slice(url.lastIndexOf('/') + 1);

export const getReferenceName = (filename: string) =>
  filename.replace(/\.[^.$]+$/, '');

export const getFileExtension = (url: string) => {
  if (!url) return '';
  const filename = getFileFromUrl(url);
  return filename.substr(filename.lastIndexOf('.') + 1);
};

export const allowedFileTypeDoc = {
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats': ['.docx']
};

export const allowedFileTypePdf = {
  'application/pdf': ['.pdf']
};

export const allowedFileTypeMixed = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats': ['.docx']
};

function getSubmissionIdFromURL(url: string) {
  // read the submission id from the path
  const urlObj = new URL(url);

  const match = urlObj.pathname.match(/\/submission\/([^/]+)/);

  return match?.[1];
}
type FileAccessOptions = {
  newtab?: boolean;
  submissionContextId?: string;
  organizationId?: string;
};

const getSignedFileUrl = async (
  fileUrl: string,
  {
    submissionContextId,
    organizationId
  }: Pick<FileAccessOptions, 'submissionContextId' | 'organizationId'> = {}
) => {
  const foundSubmissionId = getSubmissionIdFromURL(fileUrl);

  if (foundSubmissionId) {
    return getSignedPrivateAccessUrl(foundSubmissionId, fileUrl);
  }

  if (organizationId) {
    return getOrganizationSignedPrivateAccessUrl(organizationId, fileUrl);
  }

  if (submissionContextId) {
    return getSignedPrivateAccessUrl(submissionContextId, fileUrl);
  }

  return fileUrl;
};

export const openFile = async (
  fileUrl: string,
  options: FileAccessOptions = { newtab: true }
) => {
  const urlToFetch = await getSignedFileUrl(fileUrl, options);

  const response = await fetch(urlToFetch);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  window.open(blobUrl, options.newtab ? '_blank' : '_self');
};

export const parseUploadError = (error: any) => {
  if (error?.response?.data?.error?.match(/ECONNREFUSED/)) {
    return 'Upload failed: Network error, please try again.';
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    'Something went wrong, please try again.'
  );
};

export const getFileMetadata = (fileData: ISubmissionFile) => {
  return pick(fileData, ['referenceName', 'name', 'extension', 'url']);
};

// New: normalize one figure entry
const getFigureMetadata = (figure: any) => {
  return {
    caption: figure?.caption,
    altText: figure?.altText,
    file: figure?.file ? getFileMetadata(figure.file) : null
  };
};

// New: normalize the whole array
export const getFiguresMetadata = (figures: IFiguresAndTablesFile) => {
  return figures && figures.map(getFigureMetadata);
};

export const downloadFile = async (
  fileUrl: string,
  options: Omit<FileAccessOptions, 'newtab'> = {}
) => {
  const signedUrl = await getSignedFileUrl(fileUrl, options);

  const a = document.createElement('a');
  a.href = signedUrl;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const getImageMeta = async (
  source: File
): Promise<{
  size: number | null;
  width: number | null;
  height: number | null;
  pixels: number | null;
  invalidSubmissionUrl?: boolean;
  extension?: string | null;
}> => {
  let size: number | null = null;
  let imageUrl = '';

  size = source.size;
  imageUrl = URL.createObjectURL(source);

  // --- Extract file extension ---
  const extension = source.name.includes('.')
    ? source.name.split('.').pop()?.toLowerCase() || null
    : null;

  // --- Extract dimensions ---
  const dims = await new Promise<{
    width: number | null;
    height: number | null;
  }>(resolve => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
      if (source instanceof File) URL.revokeObjectURL(imageUrl);
    };
    img.onerror = () => resolve({ width: null, height: null });
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
  });

  return {
    size,
    width: dims.width,
    height: dims.height,
    pixels: dims.width && dims.height ? dims.width * dims.height : null,
    extension: extension
  };
};

export const getPdfSource = async (
  fileUrl: string,
  options: Omit<FileAccessOptions, 'newtab'> = {}
): Promise<string> => {
  return getSignedFileUrl(fileUrl, options);
};

export const getAllowedFileFormats = (allowedFormats: AllowedFileFormats) => {
  return Array.from(
    new Set(
      Object.values(allowedFormats ?? {})
        .flat()
        .map(ext => ext.replace('.', '').toUpperCase())
    )
  ).join(', ');
};
